/**
 * Navigation tree — typed port of design-mockups/soft-app/src/nav.js.
 *
 * Single source of truth for the v2 sidenav. The mockup hardcoded a demo
 * workspace ("Acme Corp") and demo bots ("AcmeBot", "Acme Sales",
 * "Front desk"); this port takes them as runtime context so the tree
 * reflects the user's real workspace and bot.
 *
 * Each node:
 *   - `kind: 'leaf'`               clickable → navigates (sets pageId + URL)
 *   - `kind: 'workspace' | 'bot' | 'group'`   drillable → moves viewId only
 *
 * `to(ctx)` on a leaf returns the URL the legacy router understands (so the
 * v2 sidenav routes back into the legacy URL space — same backend, same
 * URLs, only the UI shell differs).
 */

export type NavKind = 'workspace' | 'bot' | 'group' | 'leaf';

export interface NavCtx { botId?: string; customerId?: string }

export interface NavNode {
  id: string;
  label: string;
  kind: NavKind;
  /** Single-character avatar for crumbs / list rows. */
  avatar?: string;
  /** Subtitle shown beneath the page-header title for this leaf. */
  desc?: string;
  /** Numeric badge on the right of a list row. */
  count?: number;
  /** Hide the node unless the viewer has an admin/owner role. */
  adminOnly?: boolean;
  /** Section heading this leaf groups under in the sidebar items list.
   *  Consecutive leaves sharing a section render under one divider row;
   *  the divider label changes when this value changes. Leaves with no
   *  section render flush (the bot-level rows, the workspace rows). */
  section?: string;
  children?: NavNode[];
  /** Page key — only leaves should set this. Drives `pageId`. */
  page?: string;
  /** Build the URL this leaf navigates to. */
  to?: (ctx: NavCtx) => string;
}

/** A bot summary the workspace tree can list in `bots → ...` (alongside the
 *  currently-scoped bot). Pass real bots from `/api/customer/bots` so the
 *  user sees every workspace bot in the popover. */
export interface BotSummary {
  bot_id: string;
  bot_name?: string;
}

export interface BuildTreeCtx {
  customerName?: string;
  customerId?: string;
  /** The bot currently in scope (from /b/{id} URL). */
  botId?: string;
  botName?: string;
  /** All workspace bots — used so "Your bots" shows every bot, not just the scoped one. */
  bots?: BotSummary[];
  /** Viewer is platform admin/owner — unlocks adminOnly nodes. */
  isAdmin?: boolean;
}

/**
 * Build the workspace tree for a given context.
 *
 * Shape mirrors the mockup `nav.js` exactly (Workspace → Your bots → bot →
 * Overview/Prompts/Voice/Tools/Workflow/Settings → leaves, plus workspace
 * leaves: Templates / Call logs / Operations / Sandbox / Billing).
 */
export function buildTree(ctx: BuildTreeCtx): NavNode {
  const customerLabel = ctx.customerName?.trim() || 'Workspace';
  const workspaceInitial = (customerLabel[0] || 'W').toUpperCase();

  // Always include the in-scope bot, even if the customer/bots fetch
  // hasn't returned yet or doesn't include it. Without this, landing
  // directly on /b/{id}/config would build a tree with no bot subtree,
  // and the sidebar wouldn't drill into Settings.
  const merged: BotSummary[] = ctx.bots && ctx.bots.length > 0 ? [...ctx.bots] : [];
  if (ctx.botId && !merged.some((b) => b.bot_id === ctx.botId)) {
    merged.unshift({ bot_id: ctx.botId, bot_name: ctx.botName });
  }
  const allBots = merged;

  const botChildren: NavNode[] = allBots.map((b) => {
    const isScoped = b.bot_id === ctx.botId;
    const label = b.bot_name || b.bot_id;
    const initial = (label[0] || 'B').toUpperCase();
    return {
      id: `bot:${b.bot_id}`,
      label,
      kind: 'bot' as const,
      avatar: initial,
      desc: b.bot_name ? b.bot_name : undefined,
      page: 'bot-hero',
      // Only the in-scope bot exposes its sub-tree; clicking another bot
      // navigates to its owner overview (full reload).
      children: isScoped ? botSubtree() : undefined,
      // Clicking a bot in the sidebar lands on the owner overview
      // (`/manage`), not the public dial — that one's available via
      // "Call this bot" or the open URL `/b/{id}`.
      to: ({ }: NavCtx) => `/b/${b.bot_id}/manage`,
    };
  });

  // Billing & usage, Packages — always visible. Routes resolve regardless of
  // whether a customer id sits in the URL; backend resolves customer from JWT.
  const billingLeaf: NavNode[] = [
    { id: 'team', label: 'Team', kind: 'leaf', page: 'team',
      desc: 'Invite teammates, manage access.',
      to: ({ customerId }: NavCtx) =>
        customerId ? `/customer/${customerId}#team` : `/customer#team` },
    { id: 'billing', label: 'Billing & usage', kind: 'leaf', page: 'billing',
      to: ({ customerId }: NavCtx) =>
        customerId ? `/customer/${customerId}/billing` : `/customer/billing` },
    { id: 'packages', label: 'Packages', kind: 'leaf', page: 'packages',
      to: ({ customerId }: NavCtx) =>
        customerId ? `/customer/${customerId}/packages` : `/customer/packages` },
  ];

  // Documentation — Help & embed now lives in the bot sidebar (below Settings;
  // see botSubtree). API docs stays reachable from the workspace level too, but
  // the top-level entry always routes to `/api-docs` (no bot in the path); that
  // page falls back to the currently-selected bot for its examples.
  const docLeaves: NavNode[] = [
    { id: 'api-docs', label: 'API docs', kind: 'leaf', page: 'api-docs', section: 'Documentation',
      desc: 'REST + streaming API reference.',
      to: () => '/api-docs' },
  ];


  return {
    id: 'workspace',
    label: customerLabel,
    kind: 'workspace',
    avatar: workspaceInitial,
    desc: 'Workspace',
    children: [
      {
        id: 'bots',
        label: 'Your bots',
        kind: 'group',
        page: 'customer-overview',
        desc: 'Your bots, list view.',
        children: botChildren.length > 0 ? botChildren : undefined,
        count: allBots.length || undefined,
        // Clicking the group navigates to the workspace landing AND drills
        // the sidebar to show the bots.
        to: ({ customerId }: NavCtx) => customerId ? `/customer/${customerId}` : '/',
      },
      ...billingLeaf,
      ...docLeaves,
    ],
  };
}

/** The bot subtree shape — mirrors the mockup exactly. URLs map back to
 *  the legacy router (e.g. /b/{id}/config?tab=prompts). */
function botSubtree(): NavNode[] {
  const workflowLeaves: NavNode[] = [
    { id: 'bot:workflow', label: 'Workflow', kind: 'leaf', page: 'bot-workflow',
      section: 'Voice & behavior',
      desc: 'Build the conversation graph this bot follows on calls.',
      to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=workflow` },
  ];

  return [
    { id: 'bot:overview', label: 'Overview', kind: 'leaf', page: 'bot-hero',
      desc: "Today's activity and quick links.",
      to: ({ botId }: NavCtx) => `/b/${botId}/manage` },
    { id: 'bot:dial', label: 'Call this bot', kind: 'leaf', page: 'bot-dial',
      desc: 'The public URL anyone can dial in from.',
      to: ({ botId }: NavCtx) => `/b/${botId}` },
    { id: 'bot:call-logs', label: 'Call logs', kind: 'leaf', page: 'call-logs',
      desc: "Every call this bot has answered, with audio and transcript.",
      to: ({ botId }: NavCtx) => `/b/${botId}/call-logs` },
    {
      // Outbound is a drillable menu: campaign management plus the
      // customer-wide do-not-call list.
      id: 'bot:operate', label: 'Outbound', kind: 'group', page: 'operate',
      desc: 'Outbound campaigns and dialing sequences.',
      to: ({ botId }: NavCtx) => `/b/${botId}/operate`,
      children: [
        { id: 'bot:operate-campaigns', label: 'Outbound Campaigns', kind: 'leaf', page: 'operate',
          desc: 'Open a campaign to edit its dialing sequence, or start a new one.',
          to: ({ botId }: NavCtx) => `/b/${botId}/operate` },
        { id: 'bot:dnc', label: 'Do Not Call', kind: 'leaf', page: 'operate-dnc',
          desc: 'Numbers suppressed from every outbound campaign.',
          to: ({ botId }: NavCtx) => `/b/${botId}/operate/do-not-call` },
      ],
    },
    // Demos hidden from the navbar (too many links). The /b/{id}/demo-N routes
    // still work — re-add nav leaves here if they need to be discoverable again.
    {
      // Settings is now the umbrella for everything that shapes how the
      // bot speaks, listens, and acts, including the admin-only conversation
      // workflow editor. "What it says" / Voice / Tools used to be top-level
      // siblings of Settings; folded inside to keep bot navigation compact.
      id: 'bot:settings', label: 'Settings', kind: 'group', page: 'bot-prompts',
      desc: 'How it talks, how it sounds, what it can do, plus brand and integrations.',
      to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=prompts`,
      // Leaves are grouped under four section dividers (rendered by Sidenav
      // from the `section` field). Order matters: leaves must be listed in
      // section order so consecutive same-section rows sit together.
      //   Voice & behavior — what it says / how it sounds
      //   Calls            — everything that happens on a live call
      //   Setup            — one-time configuration: brand, templates, tools…
      //   Advanced         — power-user knobs
      children: [
        // ── Voice & behavior ──
        { id: 'bot:prompts', label: 'What it says', kind: 'leaf', page: 'bot-prompts',
          section: 'Voice & behavior',
          desc: 'Standing instructions, greeting, documents it can quote.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=prompts` },
        { id: 'bot:voice', label: 'Voice', kind: 'leaf', page: 'bot-voice',
          section: 'Voice & behavior',
          desc: 'Pick a voice, tune pace and warmth, and manage personas.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=voices` },
        { id: 'bot:pronunciation', label: 'Pronunciation', kind: 'leaf', page: 'bot-pronunciation',
          section: 'Voice & behavior',
          desc: 'Brand names, place names, acronyms — how the bot should say them.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=glossary` },
        { id: 'bot:languages', label: 'Languages', kind: 'leaf', page: 'bot-languages',
          section: 'Voice & behavior',
          desc: 'Languages the bot speaks.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=languages` },
        ...workflowLeaves,
        // ── Calls ──
        { id: 'bot:telephony', label: 'Telephony', kind: 'leaf', page: 'bot-telephony',
          section: 'Calls',
          desc: 'Phone number, provider setup — Twilio, Cloudonix, Asterisk.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=telephony` },
        { id: 'bot:calls', label: 'When it picks up', kind: 'leaf', page: 'bot-calls',
          section: 'Calls',
          desc: 'Greet timing, idle handling, hold, recording, keypad input.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=calls` },
        { id: 'bot:escalation', label: 'When it calls', kind: 'leaf', page: 'bot-escalation',
          section: 'Calls',
          desc: 'Hand off to a person — escalation number + press-a-key transfer menu.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=escalation` },
        { id: 'bot:session', label: 'Memory & sessions', kind: 'leaf', page: 'bot-session',
          section: 'Calls',
          desc: 'How much it remembers between calls.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=session` },
        { id: 'bot:tuner', label: 'Tuner', kind: 'leaf', page: 'bot-tuner',
          section: 'Calls',
          desc: 'Analyze recent calls with AI and improve the prompt.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=tuner` },
        // ── Passive ──
        { id: 'bot:passive', label: 'Passive', kind: 'leaf', page: 'bot-passive',
          section: 'Passive',
          desc: 'Answer without speaking — transcript webhook, or silent tool-running.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=passive` },
        // ── Chat ──
        { id: 'bot:chat', label: 'Chat & takeover', kind: 'leaf', page: 'bot-chat',
          section: 'Chat',
          desc: 'WhatsApp, Telegram and SMS conversations, human takeover, escalation contacts.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=chat` },
        // ── Setup ──
        { id: 'bot:templates', label: 'Templates', kind: 'leaf', page: 'bot:templates',
          section: 'Setup',
          desc: 'Apply a starting config or save your own.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=templates` },
        { id: 'bot:brand', label: 'Brand & name', kind: 'leaf', page: 'bot-brand',
          section: 'Setup',
          desc: 'Name, logo.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=general` },
        { id: 'bot:compliance', label: 'What it discloses', kind: 'leaf', page: 'bot-compliance',
          section: 'Setup',
          desc: 'Recording disclosures, embed access, abuse limits.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=governance` },
        { id: 'bot:tools', label: 'Tools', kind: 'leaf', page: 'bot-tools',
          section: 'Setup',
          desc: 'Actions the bot can take during a call.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=tools` },
        { id: 'bot:integrations', label: 'Integrations', kind: 'leaf', page: 'bot-integrations',
          section: 'Setup',
          desc: 'HubSpot, Twilio, Slack…',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=integrations` },
        // ── Advanced ──
        { id: 'bot:advanced', label: 'Advanced', kind: 'leaf', page: 'bot-advanced',
          section: 'Advanced',
          desc: 'Power-user settings.',
          to: ({ botId }: NavCtx) => `/b/${botId}/config?tab=advanced` },
      ],
    },
    // Sit below Settings in the bot sidebar. Help & embed is bot-only; API docs
    // is also reachable from the workspace level (see docLeaves).
    { id: 'bot:help', label: 'Help & embed', kind: 'leaf', page: 'help',
      desc: 'Embed snippets — drop your bot onto any page.',
      to: ({ botId }: NavCtx) => `/b/${botId}/help` },
    { id: 'bot:api-docs', label: 'API docs', kind: 'leaf', page: 'api-docs',
      desc: 'REST + streaming API reference.',
      to: ({ botId }: NavCtx) => `/b/${botId}/api-docs` },
  ];
}

// ──────────────────────────────────────────────────────────────
//  Tree helpers — direct ports of the mockup's helpers in nav.js.
// ──────────────────────────────────────────────────────────────

/** Path from root to id (inclusive). Null if not found. */
export function findPath(root: NavNode, id: string, trail: NavNode[] = []): NavNode[] | null {
  const next = [...trail, root];
  if (root.id === id) return next;
  if (!root.children) return null;
  for (const c of root.children) {
    const r = findPath(c, id, next);
    if (r) return r;
  }
  return null;
}

/** Path from root to the first leaf whose `page` key matches. Useful for
 *  callers that know the page-key (`bot-prompts`) but not the node id
 *  (`bot:prompts`) — e.g. URL → page-key mapping in route dispatchers. */
export function findPathByPage(root: NavNode, page: string, trail: NavNode[] = []): NavNode[] | null {
  const next = [...trail, root];
  if (root.page === page) return next;
  if (!root.children) return null;
  for (const c of root.children) {
    const r = findPathByPage(c, page, next);
    if (r) return r;
  }
  return null;
}

/** Convenience — try `findPath` first, fall back to `findPathByPage`. */
export function findPathByIdOrPage(root: NavNode, key: string): NavNode[] | null {
  return findPath(root, key) ?? findPathByPage(root, key);
}

/** Children to display in the sidebar items list for the current view.
 *  When `viewId` is a leaf, returns its siblings. Filters adminOnly out. */
export function levelChildren(root: NavNode, viewId: string, isAdmin = false): NavNode[] {
  const trail = findPath(root, viewId);
  let kids: NavNode[] | undefined;
  if (!trail) kids = root.children;
  else {
    const last = trail[trail.length - 1];
    if (last.kind === 'leaf' || !last.children || last.children.length === 0) {
      kids = trail.length < 2 ? root.children : trail[trail.length - 2].children;
    } else {
      kids = last.children;
    }
  }
  return (kids || []).filter((n) => !n.adminOnly || isAdmin);
}

/** Breadcrumbs above the items list. Leaves drop out (they're the active row). */
export function crumbsFor(root: NavNode, viewId: string): NavNode[] {
  const trail = findPath(root, viewId) ?? [root];
  const last = trail[trail.length - 1];
  if (last.kind === 'leaf') return trail.slice(0, -1);
  return trail;
}

/** Section heading above the items list ("In Voice", "This bot", etc.). */
export function levelLabel(root: NavNode, viewId: string): string {
  const trail = findPath(root, viewId) ?? [root];
  const last = trail[trail.length - 1];
  if (last.kind === 'leaf') {
    if (trail.length < 2) return 'Workspace';
    return 'In ' + trail[trail.length - 2].label;
  }
  if (last.kind === 'workspace') return 'Workspace';
  if (last.kind === 'bot') return 'This bot';
  return 'In ' + last.label;
}

/** True if `id` (or any ancestor) has adminOnly === true. */
export function isAdminOnly(root: NavNode, id: string): boolean {
  const path = findPath(root, id);
  return !!path?.some((n) => n.adminOnly);
}
